/**
 * 총무 업무 편집 — 지점마다 시간·내용이 다르므로 매장이 직접 고친다.
 *
 * 여기서 등록한 항목이 대시보드(실시간 현황) 타임라인에 시간 순서대로 표시된다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';
import { authedFetch } from '../../../infrastructure/api/authedFetch';
import './StaffTaskSettingsPage.css';

interface StaffTaskTemplate {
  id: string;
  shift: string;
  shift_label: string;
  start_time: string;
  end_time: string | null;
  place: string;
  content: string;
  weekdays: string;
  display_order: number;
  is_active: boolean;
  is_patrol: boolean;
}

interface FormState {
  shift: string;
  start_time: string;
  end_time: string;
  place: string;
  content: string;
  weekdays: string;
  is_patrol: boolean;
}

const SHIFTS: { value: string; label: string }[] = [
  { value: 'morning', label: '오전' },
  { value: 'middle', label: '미들' },
  { value: 'night', label: '야간' },
  { value: 'closing', label: '마감' },
];

const WEEKDAY_PRESETS: { value: string; label: string }[] = [
  { value: '', label: '매일' },
  { value: '1,2,3,4,5', label: '평일' },
  { value: '6', label: '토요일' },
  { value: '0', label: '일요일' },
];

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

const EMPTY_FORM: FormState = {
  shift: 'morning',
  start_time: '',
  end_time: '',
  place: '',
  content: '',
  weekdays: '',
  is_patrol: false,
};

const weekdayLabel = (weekdays: string) => {
  if (!weekdays.trim()) return '매일';
  const preset = WEEKDAY_PRESETS.find((p) => p.value === weekdays);
  if (preset) return preset.label;
  return weekdays
    .split(',')
    .map((d) => DAY_NAMES[Number(d.trim())] || '')
    .filter(Boolean)
    .join('·');
};

const StaffTaskSettingsPage: React.FC = () => {
  const [templates, setTemplates] = useState<StaffTaskTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmPreset, setConfirmPreset] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [patrolCandidates, setPatrolCandidates] = useState<StaffTaskTemplate[]>([]);
  // 2026-10-05 대표 결정 — 한 번에 다 연결하지 않고 원장이 목록을 보고 고른다
  const [showLinkList, setShowLinkList] = useState(false);
  const [linkPicked, setLinkPicked] = useState<Set<string>>(new Set());

  const apiCall = useCallback(async (path: string, init?: RequestInit) => {
    const res = await authedFetch(`${API_BASE_URL}/api/v1/owner/staff-tasks${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers || {}),
      },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(typeof json.detail === 'string' ? json.detail : '요청을 처리하지 못했습니다.');
    }
    return json;
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const json = await apiCall('/templates');
      setTemplates(json.data || []);
      // '순찰'이 들어간 칸 중 아직 QR 순찰과 연결 안 된 것 — 원장이 보고 한 번에 연결한다
      const cand = await apiCall('/templates/patrol-candidates').catch(() => ({ data: [] }));
      setPatrolCandidates(cand.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : '업무 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (text: string) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  const validate = (form: FormState): string => {
    if (!form.start_time) return '시작 시각을 입력해 주세요.';
    if (!form.content.trim()) return '업무 내용을 입력해 주세요.';
    if (form.end_time && form.end_time < form.start_time) {
      return '종료 시각이 시작 시각보다 빠릅니다.';
    }
    return '';
  };

  const handleAdd = async () => {
    const invalid = validate(addForm);
    if (invalid) {
      setError(invalid);
      return;
    }
    try {
      setSaving(true);
      setError('');
      await apiCall('/templates', {
        method: 'POST',
        body: JSON.stringify({ ...addForm, end_time: addForm.end_time || null }),
      });
      setAddForm(EMPTY_FORM);
      setShowAddForm(false);
      await load();
      notify('업무를 추가했습니다.');
    } catch (e) {
      setError(e instanceof Error ? e.message : '추가하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t: StaffTaskTemplate) => {
    setEditingId(t.id);
    setConfirmDeleteId(null);
    setEditForm({
      shift: t.shift,
      start_time: t.start_time,
      end_time: t.end_time || '',
      place: t.place,
      content: t.content,
      weekdays: t.weekdays,
      is_patrol: !!t.is_patrol,
    });
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    const invalid = validate(editForm);
    if (invalid) {
      setError(invalid);
      return;
    }
    try {
      setSaving(true);
      setError('');
      await apiCall(`/templates/${editingId}`, {
        method: 'PUT',
        body: JSON.stringify({ ...editForm, end_time: editForm.end_time || null }),
      });
      setEditingId(null);
      await load();
      notify('수정했습니다.');
    } catch (e) {
      setError(e instanceof Error ? e.message : '수정하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setSaving(true);
      setError('');
      await apiCall(`/templates/${id}`, { method: 'DELETE' });
      setConfirmDeleteId(null);
      await load();
      notify('업무를 목록에서 내렸습니다.');
    } catch (e) {
      setError(e instanceof Error ? e.message : '삭제하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkPatrol = async () => {
    try {
      setSaving(true);
      setError('');
      const json = await apiCall('/templates/link-patrol', {
        method: 'POST',
        body: JSON.stringify({ ids: Array.from(linkPicked) }),
      });
      setShowLinkList(false);
      await load();
      notify(`순찰 칸 ${json.data?.linked || 0}개를 QR 순찰과 연결했습니다.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : '연결하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    try {
      setSaving(true);
      setError('');
      const json = await apiCall('/templates/clear', { method: 'POST' });
      setConfirmClear(false);
      await load();
      notify(`업무 ${json.data?.cleared || 0}건을 비웠습니다. 필요한 업무만 새로 추가하세요.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : '목록을 비우지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreset = async (replaceAll: boolean) => {
    try {
      setSaving(true);
      setError('');
      const json = await apiCall(`/templates/preset?replace_all=${replaceAll}`, { method: 'POST' });
      setConfirmPreset(false);
      await load();
      notify(`기본 업무표 ${json.data?.created || 0}건을 불러왔습니다.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : '기본 업무표를 불러오지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const renderForm = (
    form: FormState,
    setForm: React.Dispatch<React.SetStateAction<FormState>>,
    onSubmit: () => void,
    onCancel: () => void,
    submitLabel: string
  ) => (
    <div className="sts-form">
      <div className="sts-form-row">
        <label>
          <span>근무</span>
          <select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })}>
            {SHIFTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>시작</span>
          <input
            type="time"
            value={form.start_time}
            onChange={(e) => setForm({ ...form, start_time: e.target.value })}
          />
        </label>
        <label>
          <span>종료 (선택)</span>
          <input
            type="time"
            value={form.end_time}
            onChange={(e) => setForm({ ...form, end_time: e.target.value })}
          />
        </label>
        <label>
          <span>요일</span>
          <select
            value={WEEKDAY_PRESETS.some((p) => p.value === form.weekdays) ? form.weekdays : ''}
            onChange={(e) => setForm({ ...form, weekdays: e.target.value })}
          >
            {WEEKDAY_PRESETS.map((p) => (
              <option key={p.label} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="sts-form-row">
        <label className="sts-place">
          <span>장소·구분 (선택)</span>
          <input
            type="text"
            maxLength={30}
            placeholder="예: 학습실, 카페라운지, 1교시-1순찰"
            value={form.place}
            onChange={(e) => setForm({ ...form, place: e.target.value })}
          />
        </label>
        <label className="sts-content">
          <span>업무 내용</span>
          <input
            type="text"
            placeholder="예: 온도체크 후 냉난방기 조절해주세요"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
          />
        </label>
      </div>
      <label className="sts-patrol-check">
        <input
          type="checkbox"
          checked={form.is_patrol}
          onChange={(e) => setForm({ ...form, is_patrol: e.target.checked })}
        />
        <span>
          🚶 순찰 칸 — QR 순찰로 확인
          <em>같은 시간대 순찰 칸은 한 회차로 묶이고, 그 시간에 모든 구역 QR 을 찍으면 자동으로 체크됩니다.</em>
        </span>
      </label>
      <div className="sts-form-actions">
        <button className="sts-btn primary" disabled={saving} onClick={onSubmit}>
          {submitLabel}
        </button>
        <button className="sts-btn" disabled={saving} onClick={onCancel}>
          취소
        </button>
      </div>
    </div>
  );

  const grouped = SHIFTS.map((s) => ({
    ...s,
    items: templates.filter((t) => t.shift === s.value),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="staff-task-settings">
      <div className="sts-header">
        <div>
          <h1>🧹 총무 업무 편집</h1>
          <p className="sts-sub">
            여기서 등록한 업무가 대시보드(실시간 현황)에 시간 순서대로 표시되고, 그 시간이 되면 불이
            들어옵니다. 지점 사정에 맞게 시간과 내용을 고치세요.
          </p>
        </div>
        <button
          className="sts-btn primary"
          onClick={() => {
            setShowAddForm((v) => !v);
            setEditingId(null);
          }}
        >
          {showAddForm ? '닫기' : '+ 업무 추가'}
        </button>
      </div>

      {error && <div className="sts-alert error">{error}</div>}
      {message && <div className="sts-alert ok">{message}</div>}

      {patrolCandidates.length > 0 && (
        <div className="sts-patrol-banner">
          <div>
            <strong>🚶 순찰로 보이는 칸 {patrolCandidates.length}개가 QR 순찰과 아직 연결되지 않았습니다.</strong>
            <p>
              장소·구분이나 내용에 '순찰'이 들어간 칸입니다. 연결하면 그 시간에 모든 구역 QR 을 찍었을 때 자동으로
              체크되고, [순찰 QR] 순찰표의 회차가 됩니다.
            </p>
          </div>
          {!showLinkList && (
            <button
              className="sts-btn primary"
              disabled={saving}
              onClick={() => {
                setLinkPicked(new Set(patrolCandidates.map((t) => t.id)));
                setShowLinkList(true);
              }}
            >
              목록 보고 연결
            </button>
          )}
        </div>
      )}

      {patrolCandidates.length > 0 && showLinkList && (
        <div className="sts-patrol-banner" style={{ display: 'block' }}>
          <strong>순찰로 연결할 칸을 고르세요</strong>
          <p>순찰이 아닌 칸은 체크를 푸세요. 체크한 칸만 QR 순찰과 연결됩니다.</p>
          <div style={{ margin: '8px 0' }}>
            {patrolCandidates.map((t) => (
              <label key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
                <input
                  type="checkbox"
                  checked={linkPicked.has(t.id)}
                  onChange={(e) => {
                    const next = new Set(linkPicked);
                    if (e.target.checked) next.add(t.id);
                    else next.delete(t.id);
                    setLinkPicked(next);
                  }}
                />
                <span>
                  {t.shift_label} · {t.start_time}
                  {t.end_time ? `~${t.end_time}` : ''} · {t.place} — {t.content}
                </span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="sts-btn primary"
              disabled={saving || linkPicked.size === 0}
              onClick={handleLinkPatrol}
            >
              선택한 {linkPicked.size}개 연결
            </button>
            <button className="sts-btn" disabled={saving} onClick={() => setShowLinkList(false)}>
              닫기
            </button>
          </div>
        </div>
      )}

      {showAddForm &&
        renderForm(addForm, setAddForm, handleAdd, () => setShowAddForm(false), '추가')}

      {loading ? (
        <p className="sts-empty">불러오는 중...</p>
      ) : templates.length === 0 ? (
        <div className="sts-empty-box">
          <p>등록된 업무가 없습니다.</p>
          <p className="sts-hint">
            운영 중인 지점에서 쓰는 기본 업무표({131}건)를 그대로 가져온 뒤 고쳐 쓰는 것이 빠릅니다.
          </p>
          <button className="sts-btn primary" disabled={saving} onClick={() => handlePreset(false)}>
            기본 업무표 불러오기
          </button>
        </div>
      ) : (
        <>
          <div className="sts-summary">
            총 {templates.length}건
            {!confirmClear ? (
              <button className="sts-btn link" onClick={() => { setConfirmClear(true); setConfirmPreset(false); }}>
                목록 비우기
              </button>
            ) : (
              <span className="sts-confirm">
                지금 업무 {templates.length}건을 모두 내립니다. 비운 뒤 필요한 업무만 새로 추가하세요. 진행할까요?
                <button className="sts-btn danger" disabled={saving} onClick={handleClear}>
                  비우기
                </button>
                <button className="sts-btn" onClick={() => setConfirmClear(false)}>
                  취소
                </button>
              </span>
            )}
            {!confirmPreset ? (
              <button className="sts-btn link" onClick={() => { setConfirmPreset(true); setConfirmClear(false); }}>
                기본 업무표로 전체 교체
              </button>
            ) : (
              <span className="sts-confirm">
                지금 목록을 모두 내리고 기본 업무표로 바꿉니다. 진행할까요?
                <button className="sts-btn danger" disabled={saving} onClick={() => handlePreset(true)}>
                  교체
                </button>
                <button className="sts-btn" onClick={() => setConfirmPreset(false)}>
                  취소
                </button>
              </span>
            )}
          </div>

          {grouped.map((group) => (
            <div key={group.value} className="sts-group">
              <h2>
                {group.label} <span>{group.items.length}건</span>
              </h2>
              {group.items.map((t) => (
                <div key={t.id} className="sts-item">
                  {editingId === t.id ? (
                    renderForm(
                      editForm,
                      setEditForm,
                      handleUpdate,
                      () => setEditingId(null),
                      '저장'
                    )
                  ) : (
                    <>
                      <div className="sts-item-time">
                        <strong>{t.start_time}</strong>
                        {t.end_time && <span>~{t.end_time}</span>}
                      </div>
                      <div className="sts-item-body">
                        <div className="sts-item-content">
                          {t.is_patrol && <span className="sts-patrol-tag">🚶 QR 순찰</span>}
                          {t.place && <span className="sts-tag">[{t.place}]</span>}
                          {t.content}
                        </div>
                        <div className="sts-item-meta">{weekdayLabel(t.weekdays)}</div>
                      </div>
                      <div className="sts-item-actions">
                        <button className="sts-btn" onClick={() => startEdit(t)}>
                          수정
                        </button>
                        {confirmDeleteId === t.id ? (
                          <>
                            <button
                              className="sts-btn danger"
                              disabled={saving}
                              onClick={() => handleDelete(t.id)}
                            >
                              삭제 확인
                            </button>
                            <button className="sts-btn" onClick={() => setConfirmDeleteId(null)}>
                              취소
                            </button>
                          </>
                        ) : (
                          <button className="sts-btn" onClick={() => setConfirmDeleteId(t.id)}>
                            삭제
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default StaffTaskSettingsPage;
